import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowDownRight } from "lucide-react";
import { useLang } from "@/lib/i18n";

/**
 * Floating hero clarity card.
 * 10 rotating decision-intelligence reports — no fabricated numerical scores.
 * Each report communicates qualitative indicators across decision readiness,
 * emotional influence, relationship sensitivity, life-phase, blind spot,
 * recommended focus, timing and confidence.
 */
const REPORTS = [
  {
    name: "Priya, 29",
    category: "Career uncertainty",
    indicators: [
      { label: "Decision Energy", value: "Favorable", tone: "strong" },
      { label: "Emotional Influence", value: "Moderate", tone: "neutral" },
      { label: "Current Life Phase", value: "Transition", tone: "neutral" },
    ],
    blindSpot: "You seek certainty before action.",
    focus: "Have the conversation you've been postponing this week.",
    timing: "Window opens · next 21 days",
    confidence: "Favorable",
  },
  {
    name: "Arjun, 34",
    category: "Financial decision",
    indicators: [
      { label: "Decision Energy", value: "Reflective", tone: "neutral" },
      { label: "Emotional Influence", value: "Elevated", tone: "caution" },
      { label: "Current Life Phase", value: "Consolidation", tone: "neutral" },
    ],
    blindSpot: "You postpone difficult conversations.",
    focus: "Pause major commitments for 7 days, then decide alone.",
    timing: "Hold · re-evaluate in 2 weeks",
    confidence: "Caution",
  },
  {
    name: "Neha, 31",
    category: "Career switch",
    indicators: [
      { label: "Decision Energy", value: "Expansive", tone: "strong" },
      { label: "Emotional Influence", value: "Low", tone: "strong" },
      { label: "Current Life Phase", value: "Expansion", tone: "strong" },
    ],
    blindSpot: "You overanalyze emotional risks.",
    focus: "Commit to one path. Review honestly after 30 days.",
    timing: "Act · next 14 days",
    confidence: "Strong",
  },
  {
    name: "Rohan, 27",
    category: "Entrepreneurship",
    indicators: [
      { label: "Decision Energy", value: "Transformative", tone: "strong" },
      { label: "Emotional Influence", value: "Moderate", tone: "neutral" },
      { label: "Current Life Phase", value: "Foundation", tone: "neutral" },
    ],
    blindSpot: "You ignore intuition when stressed.",
    focus: "Build the smallest possible version this week.",
    timing: "Start small · immediately",
    confidence: "Favorable",
  },
  {
    name: "Ananya, 33",
    category: "Marriage decision",
    indicators: [
      { label: "Decision Energy", value: "Reflective", tone: "neutral" },
      { label: "Relationship Sensitivity", value: "Elevated", tone: "caution" },
      { label: "Current Life Phase", value: "Reflection", tone: "neutral" },
    ],
    blindSpot: "You prioritize safety over growth.",
    focus: "Write what you'd choose if no one was watching.",
    timing: "Reflect · 30-day window",
    confidence: "Mixed",
  },
  {
    name: "Vikram, 38",
    category: "Relocation decision",
    indicators: [
      { label: "Decision Energy", value: "Cautious", tone: "caution" },
      { label: "Emotional Influence", value: "High", tone: "caution" },
      { label: "Current Life Phase", value: "Pivot", tone: "neutral" },
    ],
    blindSpot: "You seek validation before decisions.",
    focus: "Live the new city for two weeks before committing.",
    timing: "Test · before deciding",
    confidence: "Mixed",
  },
  {
    name: "Kavya, 26",
    category: "Relationship clarity",
    indicators: [
      { label: "Decision Energy", value: "Transformative", tone: "strong" },
      { label: "Relationship Sensitivity", value: "High", tone: "caution" },
      { label: "Current Life Phase", value: "Threshold", tone: "neutral" },
    ],
    blindSpot: "You fear irreversible choices.",
    focus: "Direct communication — say the thing you're protecting them from.",
    timing: "Speak · this week",
    confidence: "Strong",
  },
  {
    name: "Aditya, 41",
    category: "Family conflict",
    indicators: [
      { label: "Decision Energy", value: "Neutral", tone: "neutral" },
      { label: "Emotional Influence", value: "Elevated", tone: "caution" },
      { label: "Current Life Phase", value: "Boundary-setting", tone: "neutral" },
    ],
    blindSpot: "You delay important decisions.",
    focus: "Set one boundary you've been quietly resenting.",
    timing: "Define · next 10 days",
    confidence: "Favorable",
  },
  {
    name: "Sneha, 30",
    category: "Business decision",
    indicators: [
      { label: "Decision Energy", value: "Expansive", tone: "strong" },
      { label: "Emotional Influence", value: "Low", tone: "strong" },
      { label: "Current Life Phase", value: "Momentum", tone: "strong" },
    ],
    blindSpot: "You underestimate your abilities.",
    focus: "Take the offer — renegotiate the terms once.",
    timing: "Decide · within 7 days",
    confidence: "Strong",
  },
  {
    name: "Karan, 32",
    category: "Personal growth",
    indicators: [
      { label: "Decision Energy", value: "Reflective", tone: "neutral" },
      { label: "Emotional Influence", value: "Moderate", tone: "neutral" },
      { label: "Current Life Phase", value: "Recalibration", tone: "neutral" },
    ],
    blindSpot: "You absorb responsibilities that aren't yours.",
    focus: "Pause routines for a week. Observe what calls you.",
    timing: "Listen · next 14 days",
    confidence: "Favorable",
  },
];

const CONFIDENCE_COLOR = {
  Strong: "#34d399",
  Favorable: "#34d399",
  Mixed: "#FBE38E",
  Caution: "#f97316",
};

const TONE_STYLES = {
  strong: { color: "#34d399", dot: "#34d399" },
  neutral: { color: "#FBE38E", dot: "#FBE38E" },
  caution: { color: "#f97316", dot: "#f97316" },
};

function pickIndex(prev, len) {
  if (len <= 1) return 0;
  let next = Math.floor(Math.random() * len);
  if (next === prev) next = (next + 1) % len;
  return next;
}

export default function FloatingClarityCard() {
  const { lang, t } = useLang();
  const [idx, setIdx] = useState(() =>
    Math.floor(Math.random() * REPORTS.length)
  );

  useEffect(() => {
    const t = setInterval(() => {
      setIdx((prev) => pickIndex(prev, REPORTS.length));
    }, 7000);
    return () => clearInterval(t);
  }, []);

  const report = useMemo(() => REPORTS[idx], [idx]);
  const confColor = CONFIDENCE_COLOR[report.confidence] || "#FBE38E";

  return (
    <motion.div
      initial={{ opacity: 0, y: 24, rotateX: 8, rotateY: -8 }}
      animate={{ opacity: 1, y: 0, rotateX: 5, rotateY: -5 }}
      transition={{ duration: 1.0, ease: "easeOut", delay: 0.2 }}
      style={{ transformStyle: "preserve-3d", perspective: 1200 }}
      className="relative w-full max-w-[295px] mx-auto"
      data-testid="hero-floating-card"
    >
      {/* ambient pulsing glow */}
      <motion.div
        aria-hidden="true"
        className="absolute -inset-7 -z-10"
        animate={{ opacity: [0.65, 1, 0.65] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        style={{
          background:
            "radial-gradient(closest-side, rgba(212,175,55,0.24), transparent 70%), radial-gradient(closest-side at 20% 80%, rgba(99,102,241,0.20), transparent 70%)",
          filter: "blur(20px)",
        }}
      />

      {/* slow vertical float — a hair more travel + gentle rock */}
      <motion.div
        animate={{ y: [0, -9, 0], rotate: [0, 0.35, 0] }}
        transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut" }}
        className="rounded-3xl overflow-hidden relative"
        style={{
          background:
            "linear-gradient(160deg, rgba(19,25,43,0.92) 0%, rgba(20,17,46,0.92) 60%, rgba(35,29,80,0.92) 100%)",
          border: "1px solid rgba(212,175,55,0.32)",
          boxShadow:
            "0 24px 60px rgba(0,0,0,0.5), 0 0 48px rgba(212,175,55,0.10), inset 0 1px 0 rgba(255,255,255,0.05)",
          backdropFilter: "blur(20px)",
        }}
      >
        {/* card stars */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none opacity-40"
          style={{
            backgroundImage:
              "radial-gradient(1px 1px at 12% 18%, rgba(255,255,255,0.6) 50%, transparent), radial-gradient(1px 1px at 82% 22%, rgba(212,175,55,0.5) 50%, transparent), radial-gradient(1px 1px at 38% 86%, rgba(255,255,255,0.4) 50%, transparent), radial-gradient(1px 1px at 88% 72%, rgba(255,255,255,0.4) 50%, transparent)",
          }}
        />

        <div className="relative p-4 md:p-5">
          {/* header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-1.5">
              <Sparkles
                className="w-3 h-3"
                style={{ color: "#D4AF37" }}
                strokeWidth={1.5}
              />
              <span className="font-serif text-xs text-white">
                {lang === "hi" ? (
                  <>मंत्रणा<span style={{ color: "#D4AF37" }}> One</span></>
                ) : (
                  <>Mantrana<span style={{ color: "#D4AF37" }}> One</span></>
                )}
              </span>
            </div>
            <div className="text-[8px] tracking-[0.3em] uppercase text-slate-400">
              {t("float.header")}
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 0.97, y: 6 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: -4 }}
              transition={{ duration: 0.55, ease: "easeOut" }}
            >
              {/* name + category */}
              <div className="text-[9px] tracking-[0.25em] uppercase text-slate-500 mb-1">
                {report.category}
              </div>
              <div
                className="font-serif text-base text-white mb-4"
                data-testid="hero-card-name"
              >
                {report.name}
              </div>

              {/* qualitative indicators */}
              <div className="space-y-1.5 mb-4">
                {report.indicators.map((ind) => (
                  <Indicator key={ind.label} ind={ind} />
                ))}
              </div>

              {/* insights */}
              <div className="space-y-2.5 mb-3">
                <Insight label={t("float.blindSpot")} body={report.blindSpot} />
                <Insight label={t("float.focus")} body={report.focus} />
              </div>

              {/* footer — timing + confidence */}
              <div
                className="flex items-center justify-between gap-2 pt-3 mt-1"
                style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
              >
                <div className="min-w-0">
                  <div className="text-[8px] tracking-[0.25em] uppercase text-slate-500 mb-0.5">
                    {t("float.timing")}
                  </div>
                  <div className="font-serif text-[11px] text-white leading-tight truncate">
                    {report.timing}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[8px] tracking-[0.25em] uppercase text-slate-500 mb-0.5">
                    {t("float.confidence")}
                  </div>
                  <div className="font-serif text-xs text-white inline-flex items-center gap-1">
                    {report.confidence}
                    <ArrowDownRight
                      className="w-3 h-3 rotate-[-45deg]"
                      style={{ color: confColor }}
                      strokeWidth={2.2}
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* rotation dots */}
          <div className="flex items-center justify-center gap-1 mt-3">
            {REPORTS.map((_, i) => (
              <button
                key={i}
                aria-label={`Show report ${i + 1}`}
                onClick={() => setIdx(i)}
                className="rounded-full transition-all"
                style={{
                  width: i === idx ? 12 : 3,
                  height: 3,
                  background:
                    i === idx ? "#D4AF37" : "rgba(255,255,255,0.18)",
                }}
              />
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Indicator({ ind }) {
  const tone = TONE_STYLES[ind.tone] || TONE_STYLES.neutral;
  return (
    <div
      className="flex items-center justify-between gap-2 rounded-lg px-2.5 py-1.5"
      style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.05)",
      }}
    >
      <span className="text-[9px] tracking-[0.18em] uppercase text-slate-400 leading-tight">
        {ind.label}
      </span>
      <span className="inline-flex items-center gap-1.5 shrink-0">
        <span
          className="w-1.5 h-1.5 rounded-full"
          style={{ background: tone.dot, boxShadow: `0 0 6px ${tone.dot}` }}
        />
        <span
          className="font-serif text-[11px] leading-none"
          style={{ color: tone.color }}
        >
          {ind.value}
        </span>
      </span>
    </div>
  );
}

function Insight({ label, body }) {
  return (
    <div>
      <div
        className="text-[8px] tracking-[0.25em] uppercase mb-0.5"
        style={{ color: "#D4AF37" }}
      >
        {label}
      </div>
      <div className="font-serif text-[12px] text-white leading-snug">
        {body}
      </div>
    </div>
  );
}
